<script lang="ts">
	//Hooks
	import { onMount } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Services
	import { get_all_fw_versions, get_all_fw_versions_gen_2 } from '../../../../services/http';
	//Constants
	import { SLEEPING_TYPES } from '../../../../constants';
	//Helpers
	import {
		isWallDisplay,
		is_gen_1,
		is_gen_2,
		real_id,
		isBluTRV,
		isGatewayG3,
	} from '../../../../helpers/interface_helpers';
	import { capFirstChar } from '../../../../helpers/utilities';
	import { getFirmwareProgressMessage } from '../../../../helpers/firmware_progress_helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	//Stores
	import { updating_devices, firmwares, firmwares_gen_2 } from 'src/stores/device-runtime/process';
	import { devices } from 'src/stores/lists/devices';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { for_update } from 'src/stores/device-runtime/firmware-ui';
	//Design system components
	import { Alert, Button, ButtonV2, Loader, ProgressBar, Text } from '@Shelly/shelly-design-system';
	//Components
	import ControlUpdate from '../../../Controls/Single/ControlUpdate.svelte';
	import FirmwareUpdateFromUrl from './FirmwareUpdateFromUrl.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import { compare } from 'compare-versions';
	import FirmwareSchedulesUpdate from '../../Schedules/SchedulesG2/FirmwareSchedulesUpdate.svelte';

	import { isDevServerUser, isT1User } from 'src/helpers/user_server_conditions';
	import { isTestUser } from 'src/helpers/user_helpers';

	$: settingsStore = getDeviceSettings(id);
	export let device: Device;
	export let isOffline;

	let waitForResponse = false,
		showUpdateFromUrlModal = false,
		has_update,
		fwVersion,
		newFwVersion,
		fwVersionLabel,
		betaVersion: { build_id: string; url: string; version: string },
		testVersion: { build_id: string; url: string; version: string },
		target_fw = 'stable';

	const id = real_id(device.id);
	const gen1 = is_gen_1(device);
	const gen2 = is_gen_2(device);

	// For gateway devices (S3GW-1DBT001), check if gateway itself or any TRV on it is updating
	$: gatewayTrvDevices = isGatewayG3(device?.type)
		? Object.keys($devices).filter((deviceId) => new RegExp(`^${id}_22\\d{2}$`).test(deviceId))
		: [];
	$: gatewayOrTrvUpdating =
		isGatewayG3(device?.type) &&
		($updating_devices[id] ||
			gatewayTrvDevices.some(
				(trvId) =>
					isBluTRV($devices[trvId]?.type) &&
					($updating_devices[trvId] || $updating_devices[real_id(trvId)]),
			));

	const checkForUpdate = async (force = false) => {
		// const checkForUpdateRes: Request = await gen2Request(
		// 	device,
		// 	'shelly.checkforupdate',
		// 	{}
		// );

		// console.log('checkForUpdateRes', checkForUpdateRes);

		waitForResponse = true;
		if (gen1 && (force || ($firmwares?.ts && Date.now() > $firmwares.ts + 60000))) {
			await get_all_fw_versions();
		} else if (
			gen2 &&
			(force || ($firmwares_gen_2?.ts && Date.now() > $firmwares_gen_2.ts + 60000))
		) {
			await get_all_fw_versions_gen_2();
		}
		has_update = $for_update[id];
		fwVersion = gen2
			? $settingsStore?.sys?.device.fw_id || $settingsStore?.DeviceInfo?.fw_id
			: $settingsStore?.fw;
		fwVersionLabel = $format('fw_version', {
			values: {
				version: fwVersion,
				default: 'Your firmware version is: {version}.',
			},
		});

		const device_firmware_gen_1 = $firmwares?.[device.type] || $firmwares?.data?.[device.type];
		// special case for Wall Display adapting to Gen.2 versioning system:
		// more info in this Gitlab issue https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/739
		if (
			isWallDisplay(device.type) &&
			$settingsStore?.DeviceInfo?.ver &&
			compare('' + $settingsStore?.DeviceInfo?.ver, '1.1.2', '<')
		) {
			target_fw = 'custom';
			newFwVersion = '20230815-161731/1.2.1-ef519c0';
		} else {
			newFwVersion = gen2
				? $firmwares_gen_2[id]?.stable?.build_id
				: device_firmware_gen_1?.version;
		}

		if (isDevServerUser(['repo', 'nb']) || isT1User()) {
			const updates = gen2 ? $firmwares_gen_2[id] : device_firmware_gen_1;
			betaVersion = gen2 ? updates?.beta : { build_id: updates?.beta_ver, url: updates?.beta_url };
			testVersion = gen2 ? updates?.test : null;
		}
		waitForResponse = false;
	};

	onMount(() => {
		checkForUpdate();
	});

	let wasUpdating = false;
	$: {
		const isUpdating = !!$updating_devices[id];
		if (wasUpdating && !isUpdating) {
			checkForUpdate(true);
		}
		wasUpdating = isUpdating;
	}

	let currentFwVersion = '';
	$: if (fwVersionLabel !== undefined) {
		currentFwVersion = fwVersionLabel.split(': ')[1];
	}
</script>

{#if !fwVersion}
	<div class="d-flex justify-content-center">
		<Loader size="m" />
	</div>
{:else}
	{#if !isOffline && gen2 && SLEEPING_TYPES.indexOf(device.type) == -1 && !isWallDisplay(device.type)}
		<FirmwareSchedulesUpdate {device} />
	{/if}
	{#if SLEEPING_TYPES.indexOf(device.type) !== -1 && $updating_devices[id]}
		<Alert
			type="warning"
			class="mb-s"
			isHeadingBold
			heading={capFirstChar(
				$format('device_status_updating', {
					default: 'updating',
				}),
			)}
			message={$format('info_upate_firmware_wake_up', {
				default: 'Pending update... Wake up the device to execute the update.',
			})}
		/>
	{:else if $updating_devices[id]}
		{@const updateProgress = Math.min($updating_devices[id]?.prog ?? 0, 100)}
		<Alert
			type="info"
			class="mb-s"
			customIcon="fa-solid fa-arrows-rotate text-primary"
			isHeadingBold
			heading={capFirstChar(
				$format('device_status_updating', {
					default: 'updating',
				}),
			)}
		/>
		<Text class="mb-xs text-bold ml-s">
			{capFirstChar(getFirmwareProgressMessage(updateProgress) || '')}…
		</Text>
		<ProgressBar
			class="width-full mb-xs"
			type={updateProgress >= 100 ? 'success' : 'primary'}
			progress={updateProgress}
		/>
		<Text class="text-color-muted mb-s ml-s" styleType="body-small">
			{$format('progress_state_percent_complete', {
				default: `${updateProgress}% Complete`,
				values: { progress: updateProgress },
			})}
		</Text>
	{:else if has_update && newFwVersion}
		<Text styleType="body-small" class="mb-m ml-s">
			<span class="text-bold d-flex mb-xs"
				>{$format('span_current_firmware_version', {
					default: 'Current Firmware version:',
				})}
			</span>
			<span>{currentFwVersion}</span>
		</Text>
		<Alert
			type="info"
			class="mb-s"
			customIcon="fa-solid fa-arrows-rotate text-primary"
			isHeadingBold
			heading={capFirstChar(
				$format('firmware_available_stable', {
					default: 'available stable version:',
				}),
			)}
			message={newFwVersion}
		/>
		<ControlUpdate
			hasMarginBottomM={(!waitForResponse &&
				testVersion?.build_id &&
				fwVersion != testVersion.build_id) ||
				(!waitForResponse && betaVersion?.build_id && fwVersion != betaVersion.build_id)}
			hasMarginBottomS={true}
			{device}
			{target_fw}
			{isOffline}
		/>
	{:else if !waitForResponse}
		<Alert
			type="success"
			class={classMap({
				'mb-m':
					(!waitForResponse && testVersion?.build_id && fwVersion != testVersion.build_id) ||
					(!waitForResponse && betaVersion?.build_id && fwVersion != betaVersion.build_id),
				'mb-s': true,
			})}
			isHeadingBold
			heading={$format('device_is_updated', {
				default: 'Your device is up to date:',
			})}
			message={currentFwVersion}
		/>
	{/if}
	{#if !waitForResponse && !$updating_devices[id] && testVersion?.build_id && fwVersion != testVersion.build_id}
		<Alert
			type="warning"
			class="mb-s"
			customIcon="fa-solid fa-rotate-exclamation text-warning"
			isHeadingBold
			heading={capFirstChar(
				$format('firmware_available_test', {
					default: 'available test version:',
				}),
			)}
			message={testVersion.build_id}
		/>
		<ControlUpdate
			hasMarginBottomM={!waitForResponse &&
				betaVersion?.build_id &&
				fwVersion != betaVersion.build_id}
			hasMarginBottomS={true}
			{device}
			target_fw={testVersion.url}
			{isOffline}
		/>
	{/if}
	{#if !waitForResponse && !$updating_devices[id] && betaVersion?.build_id && fwVersion != betaVersion.build_id}
		<Alert
			type="warning"
			class="mb-s"
			customIcon="fa-solid fa-rotate-exclamation text-warning"
			isHeadingBold
			heading={capFirstChar(
				$format('firmware_available_beta', {
					default: 'available beta version:',
				}),
			)}
			message={betaVersion.build_id}
		/>
		<ControlUpdate
			hasMarginBottomM={true}
			hasMarginBottomS={true}
			{device}
			target_fw={betaVersion.url}
			{isOffline}
		/>
	{/if}
	<Button
		size="m"
		disabled={isOffline || gatewayOrTrvUpdating || !!$updating_devices[id]}
		width="full"
		variant="secondary"
		loading={waitForResponse}
		on:click={() => checkForUpdate(true)}
	>
		{capFirstChar($format('button_check_for_update', { default: 'check for update' }))}
	</Button>
	{#if isTestUser()}
		<ButtonV2
			class="mt-s"
			size="m"
			disabled={isOffline || gatewayOrTrvUpdating || !!$updating_devices[id]}
			width="full"
			variant="secondary"
			on:click={() => (showUpdateFromUrlModal = true)}
		>
			Update from URL
		</ButtonV2>
		<FirmwareUpdateFromUrl {device} {isOffline} bind:showModal={showUpdateFromUrlModal} />
	{/if}
{/if}
